import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { Skeleton } from './skeleton';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-4 py-1.5 text-sm font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        success:
          'bg-[#93c5fd]/20 border-[#93c5fd] text-[#0369a1] dark:bg-[#93c5fd]/20 dark:text-[#93c5fd]',
        green:
          'bg-emerald-100 border-emerald-200 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200',
        orange:
          'bg-orange-100 border-orange-200 text-orange-800 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-200',
        warning:
          'bg-[#fef9c3]/40 border-[#fef9c3] text-[#854d0e] dark:bg-[#fef9c3]/20 dark:text-[#fef9c3]',
        error:
          'bg-destructive/20 border-destructive/30 text-destructive dark:bg-destructive/20 dark:border-destructive/40 dark:text-red-300',
        info: 'bg-[#c4b5fd]/20 border-[#c4b5fd] text-[#6d28d9] dark:bg-[#c4b5fd]/20 dark:text-[#c4b5fd]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  isLoading = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'span';

  if (isLoading) {
    return (
      <span
        data-slot="badge"
        className="bg-muted inline-flex items-center justify-center rounded-full border border-transparent px-4 py-1.5"
      >
        <Skeleton className="h-5 w-14 rounded" />
      </span>
    );
  }

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge };
